import React from "react";
import { MapPin, Phone, MessageCircle, Mail, Globe, Info } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";
import { PHONE_DISPLAY, TEL_HREF, WA_HREF_TEXT, WA_HREF_TEXT_EN, SITE_URL, EMAIL_ADDRESS, MAPS_EMBED } from "@/constants/site";

export const Contact = () => {
  const { t, isEn } = useLanguage();

  const rows = [
    { icon: MapPin, label: t.contact.addressLabel, value: t.contact.address, testid: "contact-address-row" },
    { icon: Phone, label: t.contact.phoneLabel, value: PHONE_DISPLAY, href: TEL_HREF, testid: "contact-phone-link" },
    { icon: Mail, label: t.contact.emailLabel, value: EMAIL_ADDRESS, href: `mailto:${EMAIL_ADDRESS}`, testid: "contact-email-link" },
    { icon: Globe, label: t.contact.siteLabel, value: "www.lacantinadelbarone.it", href: SITE_URL, testid: "contact-site-link", external: true },
  ];

  return (
    <section id="contatti" className="py-24 sm:py-32 bg-warm">
      <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div>
          <Reveal>
            <Eyebrow>Contatti · Contacts</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-ink">
              {t.contact.title}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-base sm:text-lg font-light leading-relaxed text-stone-600 max-w-lg">
              {t.contact.subtitle}
            </p>
          </Reveal>

          <Reveal delay={0.22}>
            <div className="mt-9 rounded-2xl border border-gold/35 bg-cream p-6 sm:p-8 shadow-[0_20px_40px_-15px_rgba(59,10,17,0.12)]">
              <ul className="divide-y divide-stoneMuted/15">
                {rows.map(({ icon: Icon, label, value, href, testid, external }) => (
                  <li key={label} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0" data-testid={testid}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-burgundy/5 border border-gold/30">
                      <Icon className="w-4 h-4 text-burgundy" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-stoneMuted">{label}</p>
                      {href ? (
                        <a
                          href={href}
                          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                          className="text-sm sm:text-base font-medium text-ink hover:text-burgundy transition-colors break-words"
                        >
                          {value}
                        </a>
                      ) : (
                        <p className="text-sm sm:text-base font-medium text-ink">{value}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <a
                href={isEn ? WA_HREF_TEXT_EN : WA_HREF_TEXT}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-burgundy px-6 py-3.5 text-sm font-semibold text-cream hover:bg-burgundy-light transition-colors hover:scale-[1.02] active:scale-[0.98]"
              >
                <MessageCircle className="w-4 h-4" /> {t.contact.whatsapp}
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="overflow-hidden rounded-2xl border border-gold/35 shadow-[0_20px_40px_-15px_rgba(59,10,17,0.15)] bg-cream">
            <iframe
              title={t.contact.mapTitle}
              src={MAPS_EMBED}
              data-testid="contact-map-iframe"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-[360px] sm:h-[440px] border-0"
              allowFullScreen
            />
            <div className="flex items-start gap-2.5 px-5 py-4 border-t border-stoneMuted/15 bg-cream">
              <Info className="w-4 h-4 text-terracotta shrink-0 mt-0.5" />
              <p className="text-xs leading-relaxed text-stoneMuted">{t.contact.mapFallback}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
